import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Divider,
    useTheme,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import PolicyIcon from '@mui/icons-material/Policy'; // Icon for the title
import SecurityIcon from '@mui/icons-material/Security'; // Icon for section headers
import VisibilityIcon from '@mui/icons-material/Visibility'; // Icon for bullet points
import Header from '../components/Header';
import Footer from '../components/Footer';

interface PageProps {
    mode: 'light' | 'dark';
    setMode: (mode: 'light' | 'dark') => void;
}

// Custom component for a single policy section
const PolicySection = ({ title, children }: { title: string, children: React.ReactNode }) => (
    <Box mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1.5, fontSize: '1.2em' }} />
            {title}
        </Typography>
        {children}
    </Box>
);

// Custom component for a list of bullet points using MUI List
const PolicyList = ({ items, mb = 3 }: { items: string[], mb?: number }) => (
    <List sx={{ pt: 0, pb: mb, pl: 2 }}>
        {items.map((item, index) => (
            <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: '30px' }}>
                    <VisibilityIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ color: 'text.secondary', lineHeight: 1.6 }} />
            </ListItem>
        ))}
    </List>
);

const PrivacyPage = ({ mode, setMode }: PageProps) => {
    const theme = useTheme();
    const [isLoggedIn] = useState(false);

    return (
        <>
            <Header isLoggedIn={isLoggedIn} mode={mode} setMode={setMode} />

            <Box sx={{ backgroundColor: theme.palette.background.default, py: { xs: 6, md: 10 } }}>
                <Container maxWidth="md">

                    {/* Title */}
                    <Box sx={{ pb: 4, display: 'flex', alignItems: 'center' }}>
                        <PolicyIcon sx={{ mr: 2, fontSize: '3rem', color: 'primary.main' }} />
                        <Typography variant="h3" fontWeight="bold" gutterBottom color="text.primary" component="h1">
                            Privacy Policy
                        </Typography>
                    </Box>

                    {/* Introduction */}
                    <Typography variant="body1" color="text.secondary" mb={4} sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                        WorkHorse SG Pte Ltd (“**WorkHorse**”) is committed to safeguarding your personal data
                        and respecting your privacy. This Privacy Policy explains how we collect, use,
                        disclose, store, and protect your personal data in accordance with the Singapore
                        Personal Data Protection Act 2012 (**PDPA**). It is designed to help you understand why
                        we collect your information, how it is processed, and how you may exercise your rights.
                        <br /><br />
                        By accessing or using our websites, mobile applications, or any part of our services
                        (“[Services](/#)”), you consent to the practices outlined in this Privacy Policy.
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    {/* 1. Personal Data We Collect */}
                    <PolicySection title="1. Personal Data We Collect">
                        <Typography variant="body1" color="text.secondary" mb={2}>
                            “Personal Data” refers to any information that can identify you on its own or when
                            combined with other data. Depending on how you use our Services, the information we
                            collect may include:
                        </Typography>
                        <PolicyList items={[
                            'Name, address, email, and mobile number',
                            'Identification details, nationality, and date of birth',
                            'Payment information and transaction records',
                            'Automatically collected data from cookies, analytics tools, device sensors, GPS, and usage patterns',
                        ]} />
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            Personal data does not include business contact information used solely for
                            business-to-business (B2B) purposes, such as job title, business email address, or
                            office phone number.
                        </Typography>
                    </PolicySection>

                    {/* 2. How Your Data Is Collected */}
                    <PolicySection title="2. How Your Data Is Collected">
                        <Typography variant="body1" color="text.secondary" mb={2}>
                            We collect personal data when you:
                        </Typography>
                        <PolicyList items={[
                            'Create an account or submit forms',
                            'Upload documents or register for dealer services',
                            'Make enquiries or communicate with us directly',
                            'Apply for a job or interact with our Services and advertisements',
                        ]} />
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            Personal data may also be collected automatically through cookies, location features,
                            and mobile sensors. We may, where lawful, receive your information from third parties
                            such as service providers, payment processors, verification partners, or authorities.
                        </Typography>
                    </PolicySection>

                    {/* 3. Use of Your Personal Data */}
                    <PolicySection title="3. Use of Your Personal Data">
                        <Typography variant="body1" color="text.secondary" mb={2}>
                            We use your personal data for the following key purposes:
                        </Typography>
                        <PolicyList items={[
                            'Provide, operate, and improve our Services',
                            'Manage your account and verify your identity',
                            'Process requests, transactions, or listings',
                            'Enable dealer-related features including WCOIN transactions and advertisements',
                            'Support AI-driven features and generate documents and reports',
                            'Provide customer support and resolve disputes',
                            'Prevent fraud, enhance platform security, and improve user experience',
                        ]} />
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            We may also contact you with service notifications, updates, promotions, or important
                            changes. You may opt out of non-essential communications at any time.
                        </Typography>
                    </PolicySection>

                    {/* 4. Retention of Personal Data */}
                    <PolicySection title="4. Retention of Personal Data">
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            We retain your personal data only as long as necessary to fulfil the purposes for
                            which it was collected or as required by law, regulations, or accounting standards.
                            Once no longer needed, the data will be securely **deleted, anonymised, or destroyed**.
                        </Typography>
                    </PolicySection>

                    {/* 5. Cookies and Tracking Technologies */}
                    <PolicySection title="5. Cookies and Tracking Technologies">
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            Our website and applications use cookies and similar tools to enhance your browsing
                            experience, remember preferences, and analyse performance. You may disable cookies
                            in your browser settings, though some features may not function properly.
                            Cookies do not give us access to your device's files.
                        </Typography>
                    </PolicySection>

                    {/* 6. Location Information */}
                    <PolicySection title="6. Location Information">
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            Certain features may require GPS or location access. This allows us to provide
                            location-based services, verification features, route information, and tailored
                            recommendations. Location data is collected **only when explicitly permitted** by your device settings.
                        </Typography>
                    </PolicySection>

                    {/* 7. How We Protect Your Information */}
                    <PolicySection title="7. How We Protect Your Information">
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            We use technical, administrative, legal, and organisational safeguards to protect
                            your personal data. These include **encryption, secured servers, restricted access**,
                            regular system checks, and strict confidentiality agreements. While no system is completely
                            risk-free, we take reasonable measures to prevent unauthorised access, misuse, or loss.
                        </Typography>
                    </PolicySection>

                    {/* 8. Disclosure of Personal Data */}
                    <PolicySection title="8. Disclosure of Personal Data">
                        <Typography variant="body1" color="text.secondary" mb={2}>
                            WorkHorse does **not sell or rent** your data. We may share your information with trusted
                            service providers who assist in operating our platform, processing payments, running
                            analytics, marketing, or providing support—strictly for permitted purposes and under data protection agreements.
                        </Typography>
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            We may also disclose your data when required by law, to comply with regulatory
                            obligations, respond to legal requests, or protect the rights and safety of users.
                            In the event of a merger, acquisition, or restructuring, your data may be transferred
                            as part of the business assets.
                        </Typography>
                    </PolicySection>

                    {/* 9. Links to Third-Party Sites */}
                    <PolicySection title="9. Links to Third-Party Sites">
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            Our Services may contain links to third-party websites or platforms. These sites have
                            their own privacy policies, and we are not responsible for their data practices. We
                            encourage you to review their policies before interacting with them.
                        </Typography>
                    </PolicySection>

                    {/* 10. Access, Correction & Deletion */}
                    <PolicySection title="10. Access, Correction & Deletion of Your Data">
                        <Typography variant="body1" color="text.secondary" mb={2}>
                            You may request access to your personal data, corrections, deletion, or withdrawal of
                            consent at any time, subject to legal and contractual limits. For all PDPA requests, please contact our Data Protection Officer at:
                        </Typography>
                        <Box mt={2} mb={2} textAlign="center">
                            <Typography variant="h6" color="primary.main" fontWeight="bold">
                                privacy@workhorse.sg
                            </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            A small administrative fee may apply for access requests as allowed under PDPA guidelines.
                        </Typography>
                    </PolicySection>

                    {/* 11. Your Consent */}
                    <PolicySection title="11. Your Consent">
                        <Typography variant="body1" color="text.secondary" mb={3}>
                            By using our Services or submitting your personal data, you consent to the collection,
                            use, and disclosure outlined in this Privacy Policy. You may withdraw consent at any
                            time, but doing so may affect your ability to fully use certain features.
                        </Typography>
                    </PolicySection>

                    {/* 12. Updates */}
                    <PolicySection title="12. Updates to This Privacy Policy">
                        <Typography variant="body1" color="text.secondary" mb={2}>
                            WorkHorse may update this Privacy Policy to reflect changes in practices, technology,
                            or legal requirements. Updates will be published on our website with the revised
                            effective date.
                        </Typography>
                        <Box mt={1}>
                            <Typography variant="body2" color="text.primary" fontWeight="bold" sx={{ fontStyle: 'italic' }}>
                                Last Updated: 28 November 2025
                            </Typography>
                        </Box>
                    </PolicySection>

                    <Divider sx={{ my: 4 }} />

                    {/* Final Contact */}
                    <Box sx={{ pb: 4 }}>
                        <Typography variant="body1" color="text.secondary" mb={1} sx={{ fontSize: '1.1rem' }}>
                            If you have any questions, concerns, or feedback, please contact us directly:
                        </Typography>
                        <Typography variant="h6" color="text.primary" fontWeight="bold">
                            support@workhorse.sg
                        </Typography>
                    </Box>

                </Container>
            </Box>

            <Footer />
        </>
    );
};

export default PrivacyPage;